import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams, Link } from "react-router-dom"
import { Container, Row, Col, Card, CardBody, Button, Badge, Alert } from "reactstrap"
import { getAddonPurchaseStatus } from "../../api/requests/addon-requests"

const MAX_ATTEMPTS = 10
const POLL_INTERVAL_MS = 2000

const formatPrice = (v) => {
  if (v == null) return "-"
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " ₺"
}
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "Süresiz")

/**
 * Ek modül ödemesi dönüş sayfası.
 *
 * <h3>Neden yoklama (polling) var</h3>
 * PayTR'nin sunucu callback'i, kullanıcının tarayıcıya geri dönmesinden
 * <b>saniyeler sonra</b> gelebilir. Tek bir sorguyla yetinilse kayıt hâlâ
 * {@code PENDING} görünür ve başarılı bir ödeme kullanıcıya "beklemede" diye
 * gösterilirdi. Bu yüzden durum {@link MAX_ATTEMPTS} kez yoklanır.
 *
 * <p>Abonelik dönüş sayfasındaki "fallback" mekanizması burada kullanılmaz:
 * o mekanizma sayfanın daha önce açılıp açılmadığını işaretler, burada ise
 * gereken şey ödemenin gerçekten onaylandığıdır. Durum ucu bunu doğrudan
 * söylediği için ikinci bir işaretleme kaydına ihtiyaç yok.
 */
const AddonPaymentSuccess = () => {
  const [searchParams] = useSearchParams()
  const merchantOid = searchParams.get("merchant_oid") || ""
  const [addon, setAddon] = useState(null)
  const [loading, setLoading] = useState(true)
  const [invalid, setInvalid] = useState(false)
  const [stillPending, setStillPending] = useState(false)
  const timerRef = useRef(null)

  const poll = useCallback(async (oid, attempt) => {
    try {
      const res = await getAddonPurchaseStatus(oid)
      const data = res?.data
      if (!data) {
        setInvalid(true)
        setLoading(false)
        return
      }
      setAddon(data)

      if (data.status === "PENDING" && attempt < MAX_ATTEMPTS) {
        timerRef.current = setTimeout(() => poll(oid, attempt + 1), POLL_INTERVAL_MS)
        return
      }
      setStillPending(data.status === "PENDING")
      setLoading(false)
    } catch {
      setInvalid(true)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    document.title = "Ek Modül Ödemesi - Tahsilat360"
    if (!merchantOid) {
      setInvalid(true)
      setLoading(false)
      return
    }
    poll(merchantOid, 1)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [merchantOid, poll])

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)" }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" role="status" />
          <p>Ödeme durumu kontrol ediliyor...</p>
        </div>
      </div>
    )
  }

  if (invalid) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} sm={10}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: 20 }}>
                <CardBody className="p-5 text-center">
                  <Alert color="danger" className="border-0 mb-4">
                    <h4 className="alert-heading">Hata!</h4>
                    <p className="mb-0">Geçersiz işlem numarası veya satın alma kaydı bulunamadı.</p>
                  </Alert>
                  <Link to="/subscription/addons">
                    <Button color="primary">360 Market'e Dön</Button>
                  </Link>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  const failed = addon?.status === "CANCELLED"
  const active = addon?.status === "ACTIVE"

  const headerGradient = failed
    ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)"
    : stillPending
    ? "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
    : "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)"

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: headerGradient }}>
      <Container className="px-3 px-sm-0">
        <Row className="justify-content-center">
          <Col lg={6} md={8} sm={10}>
            <Card className="border-0 shadow-lg" style={{ borderRadius: 20, overflow: "hidden" }}>
              <CardBody className="text-center p-4 p-sm-5">
                <div className="d-flex align-items-center justify-content-center mb-4 mx-auto" style={{
                  width: 100, height: 100, borderRadius: "50%",
                  background: headerGradient,
                  boxShadow: "0 10px 30px rgba(0,0,0,.15)",
                }}>
                  <i className={`bx ${failed ? "bx-x" : stillPending ? "bx-time-five" : "bx-check"} text-white`}
                    style={{ fontSize: 48 }} />
                </div>

                <Alert color={failed ? "danger" : stillPending ? "warning" : "success"} className="border-0 mb-4">
                  <h4 className="alert-heading mb-1">
                    {failed ? "Ödeme Alınamadı" : stillPending ? "Ödeme Onayı Bekleniyor" : "Modülünüz Aktif!"}
                  </h4>
                  <p className="mb-0">
                    {failed
                      ? addon?.moduleName + " modülü için ödeme tamamlanamadı."
                      : stillPending
                      ? "Bankadan onay bekleniyor. Bu sayfayı kapatabilirsiniz; onay gelince modül otomatik aktifleşir."
                      : addon?.moduleName + " modülü paketinize eklendi."}
                  </p>
                </Alert>

                <Card className="border-0 bg-light mb-4">
                  <CardBody className="p-4">
                    <h6 className="text-muted mb-3"><i className="bx bx-receipt me-2" />İŞLEM DETAYLARI</h6>

                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">İşlem Numarası</label>
                      <div>
                        <Badge color={failed ? "danger" : stillPending ? "warning" : "success"}
                          className="px-3 py-2"
                          style={{ fontSize: 14, fontFamily: "'Courier New', monospace" }}>
                          {merchantOid}
                        </Badge>
                      </div>
                    </div>

                    <Row className="text-center border-top py-3">
                      <Col xs={6} className="border-end">
                        <label className="form-label text-muted small mb-1 d-block">Tutar</label>
                        <div className="h5 mb-0" style={{ color: failed ? "#dc3545" : "#71C30B" }}>
                          {formatPrice(addon?.paidAmount)}
                        </div>
                      </Col>
                      <Col xs={6}>
                        <label className="form-label text-muted small mb-1 d-block">Geçerlilik</label>
                        <div className="text-dark" style={{ fontSize: ".9rem", paddingTop: ".4rem" }}>
                          {formatDate(addon?.endDate)}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {active && (
                  <div className="mb-3 p-3 text-start" style={{
                    backgroundColor: "#F0FAE5", borderRadius: 12, border: "1px solid #71C30B40",
                  }}>
                    <div className="text-muted mb-1" style={{ fontSize: ".76rem" }}>
                      Paketinize eklenen haklar
                    </div>
                    {addon?.grantedMaxAccounts && (
                      <div style={{ fontSize: ".85rem" }}>+{addon.grantedMaxAccounts} cari / bayi</div>
                    )}
                    {addon?.grantedMaxUsers && (
                      <div style={{ fontSize: ".85rem" }}>+{addon.grantedMaxUsers} kullanıcı</div>
                    )}
                    {addon?.grantedCollectionAmount && (
                      <div style={{ fontSize: ".85rem" }}>+{formatPrice(addon.grantedCollectionAmount)} tahsilat</div>
                    )}
                    {addon?.grantedCollectionCount && (
                      <div style={{ fontSize: ".85rem" }}>+{addon.grantedCollectionCount} tahsilat adedi</div>
                    )}
                    {(addon?.grantedFeatures || []).length > 0 && (
                      <div style={{ fontSize: ".85rem" }}>{addon.grantedFeatures.length} ek özellik</div>
                    )}
                  </div>
                )}

                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                  <Link to="/subscription/addons">
                    <Button color="primary" className="px-4 py-2" style={{ fontWeight: 600 }}>
                      <i className="bx bx-extension me-2" />360 Market
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button color="light" className="px-4 py-2">
                      <i className="bx bx-home me-2" />Kontrol Paneli
                    </Button>
                  </Link>
                </div>

                <div className="mt-4 pt-4 border-top">
                  <p className="text-muted small mb-0">
                    <i className="bx bx-shield-check me-2" />
                    Bu işlem güvenli SSL bağlantısı ile gerçekleştirilmiştir
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default AddonPaymentSuccess
